import AdditionalVehiclePhotos from "./AdditionalVehiclePhotos";
import { useEffect, useRef, useState } from "react";
import {
  Camera,
  Car,
  ChevronDown,
  Mic,
  Pencil,
  Play,
  Square,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import api from "../api";
import {
  MAX_FILE_MB, MAX_PHOTOS, MAX_VOICE, MAX_FILE_BYTES, tooBig, isImageFile, filterFiles,
  pickAudioType, audioExt, uploadErrorMessage,
} from "../uploadUtils";
import "./RequestPartModal.mobile.css";

export default function RequestPartModal({ open, onClose, onSubmitted }) {
  const user = JSON.parse(localStorage.getItem("user") || "null");
  const [guestName, setGuestName] = useState("");
  const [guestContact, setGuestContact] = useState("");
  const isGuest = !localStorage.getItem("token");
  const [manualOpen, setManualOpen] = useState(false);
  const [idType, setIdType] = useState("VIN");
  const [idValue, setIdValue] = useState("");
  const [vehicleImage, setVehicleImage] = useState(null);
  const [vehiclePreview, setVehiclePreview] = useState("");
  const [additionalVehicleImages, setAdditionalVehicleImages] = useState([]); // { file, url }[]
  const additionalVehicleInputRef = useRef(null);
  const [partPhotos, setPartPhotos] = useState([]);
  const [partPreviews, setPartPreviews] = useState([]);
  const [notes, setNotes] = useState("");
  const [showNotes, setShowNotes] = useState(false);
  const [recording, setRecording] = useState(false);
  const [voiceNotes, setVoiceNotes] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const vehicleInputRef = useRef(null);
  const partInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => event.key === "Escape" && onClose?.();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  useEffect(() => () => {
    if (mediaRecorderRef.current?.state === "recording") mediaRecorderRef.current.stop();
    streamRef.current?.getTracks?.().forEach((track) => track.stop());
    if (vehiclePreview) URL.revokeObjectURL(vehiclePreview);
    partPreviews.forEach((url) => URL.revokeObjectURL(url));
    voiceNotes.forEach((item) => item.url && URL.revokeObjectURL(item.url));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!open) return null;

  const reset = () => {
    if (vehiclePreview) URL.revokeObjectURL(vehiclePreview);
    partPreviews.forEach((url) => URL.revokeObjectURL(url));
    voiceNotes.forEach((item) => item.url && URL.revokeObjectURL(item.url));
    setGuestName("");
    setGuestContact("");
    setManualOpen(false);
    setIdType("VIN");
    setIdValue("");
    setVehicleImage(null);
    setVehiclePreview("");
    additionalVehicleImages.forEach(({ url }) => URL.revokeObjectURL(url));
    setAdditionalVehicleImages([]);
    setPartPhotos([]);
    setPartPreviews([]);
    setNotes("");
    setShowNotes(false);
    setVoiceNotes([]);
    setRecording(false);
    setError("");
    setSuccess("");
  };

  const close = () => {
    if (recording) mediaRecorderRef.current?.stop();
    reset();
    onClose?.();
  };

  const handleVehicleFile = (file) => {
    if (!file) return;
    if (!isImageFile(file)) return setError("Please select an image for the vehicle/VIN plate.");
    if (tooBig(file)) return setError(`Vehicle photo must be ${MAX_FILE_MB} MB or smaller.`);
    if (vehiclePreview) URL.revokeObjectURL(vehiclePreview);
    setVehicleImage(file);
    setVehiclePreview(URL.createObjectURL(file));
    setError("");
  };

  const addAdditionalVehicleImages = (fileList) => {
    const files = Array.from(fileList || []);
    const remaining = Math.max(0, 2 - additionalVehicleImages.length);
    const valid = files.filter((file) => isImageFile(file) && !tooBig(file));
    const added = valid.slice(0, remaining);
    if (files.length !== valid.length || valid.length > remaining) {
      const message = `You can add up to 2 additional vehicle photos, each no larger than ${MAX_FILE_MB} MB.`;
      setError(message);
    } else {
      setError("");
    }
    if (added.length) setAdditionalVehicleImages((current) => [...current, ...added.map((file) => ({ file, url: URL.createObjectURL(file) }))]);
  };

  const removeAdditionalVehicleImage = (index) => {
    setAdditionalVehicleImages((current) => {
      const item = current[index];
      if (item?.url) URL.revokeObjectURL(item.url);
      return current.filter((_, i) => i !== index);
    });
  };

  const handlePartFiles = (fileList) => {
    const room = Math.max(0, MAX_PHOTOS - partPhotos.length);
    const { accepted: added, problems } = filterFiles(fileList, { room, accept: isImageFile, label: "photo" });
    if (added.length) {
      setPartPhotos((current) => [...current, ...added]);
      setPartPreviews((current) => [...current, ...added.map((file) => URL.createObjectURL(file))]);
    }
    setError(problems.join(" "));
  };

  const removePartPhoto = (index) => {
    setPartPreviews((current) => {
      if (current[index]) URL.revokeObjectURL(current[index]);
      return current.filter((_, i) => i !== index);
    });
    setPartPhotos((current) => current.filter((_, i) => i !== index));
  };

  const toggleRecording = async () => {
    if (recording) return mediaRecorderRef.current?.stop();
    if (voiceNotes.length >= MAX_VOICE) return setError(`You can add up to ${MAX_VOICE} voice notes.`);
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") return setError("Audio recording is not supported in this browser.");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeType = pickAudioType();
      const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      chunksRef.current = [];
      recorder.ondataavailable = (event) => event.data?.size > 0 && chunksRef.current.push(event.data);
      recorder.onstop = () => {
        const type = recorder.mimeType || mimeType || "audio/webm";
        const blob = new Blob(chunksRef.current, { type });
        if (blob.size > MAX_FILE_BYTES) setError(`Voice note must be ${MAX_FILE_MB} MB or smaller.`);
        else if (blob.size) setVoiceNotes((current) => [...current, { blob, type, url: URL.createObjectURL(blob) }]);
        stream.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
        setRecording(false);
      };
      mediaRecorderRef.current = recorder;
      recorder.start();
      setRecording(true);
      setError("");
    } catch {
      setError("Microphone permission was blocked or unavailable.");
    }
  };

  const removeVoice = (index) => setVoiceNotes((current) => {
    if (current[index]?.url) URL.revokeObjectURL(current[index].url);
    return current.filter((_, i) => i !== index);
  });

  const submit = async () => {
    if (!vehicleImage && !idValue.trim()) return setError("Add a VIN/registration photo or enter VIN/Rego manually.");
    if (!notes.trim() && partPhotos.length === 0 && voiceNotes.length === 0) return setError("Tell us which part you need using voice, photos or a note.");
    if (recording) return setError("Stop the voice recording before submitting.");
    if (isGuest && (!guestName.trim() || !guestContact.trim())) return setError("Please enter your name and email or phone number.");

    if (additionalVehicleImages.length > 2) return setError("Maximum 3 vehicle images, including VIN/Rego photo.");
    const fd = new FormData();
    fd.append("vehicleMethod", vehicleImage ? "photo" : "manual");
    fd.append("idType", idType);
    if (idType === "VIN") fd.append("vin", idValue.trim());
    else fd.append("rego", idValue.trim());
    fd.append("notes", notes.trim());
    fd.append("customerContact", isGuest ? guestContact.trim() : (user?.email || user?.mobile || ""));
    if (isGuest) fd.append("customerName", guestName.trim());
    if (vehicleImage) fd.append("vehicleImage", vehicleImage);
    additionalVehicleImages.forEach(({ file }) => fd.append("additionalVehicleImages", file));
    partPhotos.forEach((file) => fd.append("partPhotos", file));
    voiceNotes.forEach((voice, index) => fd.append("voiceNotes", voice.blob, `voice-${index + 1}.${audioExt(voice.type)}`));

    try {
      setSubmitting(true);
      setError("");
      await api.post(isGuest ? "/requests/guest" : "/requests", fd, { timeout: 180000 });
      setSuccess("Your part request has been submitted successfully.");
      setTimeout(() => {
        reset();
        onClose?.();
        onSubmitted?.();
      }, 700);
    } catch (err) {
      setError(uploadErrorMessage(err, "Could not submit your request. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="agp-modal-backdrop" onMouseDown={(event) => event.target === event.currentTarget && close()}>
      <div className="agp-request-modal" role="dialog" aria-modal="true" aria-label="Request part">
        <button className="agp-modal-close" onClick={close} aria-label="Close"><X size={20} /></button>
        <div className="agp-request-modal__hero">
          <div><h2><strong>Need Used</strong> Auto Parts</h2><p>Quality Parts. Lower Prices.</p></div>
        </div>

        <div className="agp-request-modal__body">
          <div className="agp-request-choice">
            <div className="agp-step-title"><b>1</b><h3>Which Vehicle?</h3><span className="agp-step-icon"><Car size={18} /></span></div>
            <button type="button" className="agp-upload-box" onClick={() => vehicleInputRef.current?.click()}>
              {vehiclePreview ? <img src={vehiclePreview} alt="Vehicle" className="agp-request-preview" /> : <><span className="camera-dot"><Camera size={20} /></span><strong>Take a picture of your VIN plate / Registration</strong><small>Fast way, snap it, get it done.</small><span className="agp-upload-hint"><Upload size={12} /> Click to upload</span></>}
            </button>
            <input ref={vehicleInputRef} type="file" accept="image/*" hidden onChange={(event) => { handleVehicleFile(event.target.files?.[0]); event.target.value = ""; }} />
            <AdditionalVehiclePhotos images={additionalVehicleImages} onAdd={addAdditionalVehicleImages} onRemove={removeAdditionalVehicleImage} inputRef={additionalVehicleInputRef} disabled={submitting} />
            <div className="agp-or"><span />OR<span /></div>
            <button type="button" className="agp-link-button" onClick={() => setManualOpen((value) => !value)}>{manualOpen ? "Hide manual entry" : "Use manual VIN / Rego entry"} <ChevronDown size={13} /></button>
            {manualOpen && <div className="agp-manual-entry"><div className="agp-manual-tabs"><button className={idType === "VIN" ? "active" : ""} onClick={() => setIdType("VIN")}>VIN</button><button className={idType === "Rego" ? "active" : ""} onClick={() => setIdType("Rego")}>Rego</button></div><input value={idValue} onChange={(event) => setIdValue(event.target.value)} placeholder={idType === "VIN" ? "e.g. 1HGCM82633A004352" : "e.g. REG-82633A004351"} /></div>}
          </div>

          <div className="agp-request-choice agp-request-choice--right">
            <div className="agp-step-title"><b>2</b><h3>What part do you need?</h3><span className="agp-step-icon agp-step-icon--orange"><Mic size={18} /></span></div>
            <button type="button" className={`agp-upload-box orange ${recording ? "recording" : ""}`} onClick={toggleRecording}>
              <span className="camera-dot">{recording ? <Square size={17} /> : <Mic size={20} />}</span>
              <strong>{recording ? "Recording… tap to stop" : "Tap to record a voice message"}</strong>
              <small>Tell us what part you need.</small>
            </button>

            {voiceNotes.length > 0 && <div className="agp-voice-list">{voiceNotes.map((voice, index) => <div key={index}><Play size={14} /><audio src={voice.url} controls /><button onClick={() => removeVoice(index)}><Trash2 size={14} /></button></div>)}</div>}

            <div className="agp-mini-actions">
              <button type="button" onClick={() => partInputRef.current?.click()}><Camera size={16} /> {partPhotos.length ? `${partPhotos.length} Photo${partPhotos.length > 1 ? "s" : ""}` : "Add Photos"}</button>
              <button type="button" onClick={() => setShowNotes((value) => !value)}><Pencil size={16} /> Add Note</button>
            </div>
            <input ref={partInputRef} type="file" accept="image/*" multiple hidden onChange={(event) => { handlePartFiles(event.target.files); event.target.value = ""; }} />

            {partPreviews.length > 0 && <div className="agp-photo-previews">{partPreviews.map((src, index) => <div key={src}><img src={src} alt="Part" /><button onClick={() => removePartPhoto(index)}><X size={11} /></button></div>)}</div>}
            {showNotes && <textarea value={notes} onChange={(event) => setNotes(event.target.value)} rows={3} placeholder="Describe the part you need…" className="agp-request-note" />}
          </div>

          {isGuest && <div className="agp-request-choice" style={{ marginBottom: "12px" }}>
            <div className="agp-step-title"><b>3</b><h3>Your contact details</h3></div>
            <div style={{ display: "grid", gap: "10px", padding: "12px" }}>
              <input aria-label="Your name" autoComplete="name" required value={guestName} onChange={(event) => setGuestName(event.target.value)} placeholder="Your full name *" className="agp-request-note" />
              <input aria-label="Email or phone number" autoComplete="email" required value={guestContact} onChange={(event) => setGuestContact(event.target.value)} placeholder="Email or phone number *" className="agp-request-note" />
            </div>
          </div>}
          {(error || success) && <div className={`agp-request-message ${success ? "success" : "error"}`}>{success || error}</div>}
          <button className="agp-find-wide" disabled={submitting} onClick={submit}>{submitting ? "Submitting…" : "Find My Part"}</button>
        </div>
      </div>
    </div>
  );
}
